import React, { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, Calendar, Users, Gavel } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

export type RecordType = 'document' | 'meeting' | 'resolution' | 'journal';

interface CollapsibleRecordRowProps {
  id: string;
  title: string;
  date: string | Date;
  type: RecordType;
  badge?: {
    label: string;
    variant?: 'default' | 'secondary' | 'outline' | 'destructive';
    className?: string;
  };
  subtitle?: string;
  expandedContent?: React.ReactNode;
  onClick?: () => void;
  actions?: React.ReactNode;
}

const typeIcons: Record<RecordType, React.ElementType> = {
  document: FileText,
  meeting: Users,
  resolution: Gavel,
  journal: FileText,
};

export const CollapsibleRecordRow: React.FC<CollapsibleRecordRowProps> = ({
  id,
  title,
  date,
  type,
  badge,
  subtitle,
  expandedContent,
  onClick,
  actions,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = typeIcons[type];
  
  // Safely format date, handling invalid/null values
  const getFormattedDate = () => {
    if (!date) return 'No date';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return 'Invalid date';
      return format(dateObj, 'MMM d, yyyy');
    } catch {
      return 'Invalid date';
    }
  };
  const formattedDate = getFormattedDate();

  const handleRowClick = () => {
    if (onClick) {
      onClick();
    } else if (expandedContent) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleExpandToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="border rounded-lg overflow-hidden transition-all">
      <div
        className={cn(
          "flex items-center justify-between p-3 hover:bg-accent cursor-pointer transition-colors",
          isExpanded && "border-b bg-accent/50"
        )}
        onClick={handleRowClick}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {expandedContent && (
            <button
              onClick={handleExpandToggle}
              className="p-1 hover:bg-accent rounded shrink-0"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          )}
          <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{title}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          <span className="text-xs text-muted-foreground hidden sm:inline">
            <Calendar className="h-3 w-3 inline mr-1" />
            {formattedDate}
          </span>
          {badge && (
            <Badge 
              variant={badge.variant || 'secondary'} 
              className={cn("text-xs", badge.className)}
            >
              {badge.label}
            </Badge>
          )}
          {actions}
        </div>
      </div>
      {isExpanded && expandedContent && (
        <div className="p-4 bg-muted/30">
          {expandedContent}
        </div>
      )}
    </div>
  );
};

interface CollapsibleRecordListProps {
  children: React.ReactNode;
  maxVisible?: number;
  showAllLabel?: string;
  onShowAll?: () => void;
}

export const CollapsibleRecordList: React.FC<CollapsibleRecordListProps> = ({
  children,
  maxVisible = 5,
  showAllLabel = "View All",
  onShowAll,
}) => {
  const [showAll, setShowAll] = useState(false);
  const childArray = React.Children.toArray(children);
  const hasMore = childArray.length > maxVisible;
  const visibleChildren = showAll ? childArray : childArray.slice(0, maxVisible);

  return (
    <div className="space-y-2">
      {visibleChildren}
      {hasMore && !showAll && (
        <Button
          variant="ghost"
          className="w-full text-sm text-muted-foreground hover:text-foreground"
          onClick={() => {
            if (onShowAll) {
              onShowAll();
            } else {
              setShowAll(true);
            }
          }}
        >
          {showAllLabel} ({childArray.length - maxVisible} more)
        </Button>
      )}
      {showAll && hasMore && (
        <Button
          variant="ghost"
          className="w-full text-sm text-muted-foreground hover:text-foreground"
          onClick={() => setShowAll(false)}
        >
          Show Less
        </Button>
      )}
    </div>
  );
};
